import { CSSProperties } from 'react'

import theme from '../../styles/themes/default.theme'
import { AccordionAlertType } from './Accordion.types'

const styles = {
  accordionContainer: (footerPresent: boolean, alertType?: AccordionAlertType, hasBorder?: boolean) => ({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    backgroundColor: footerPresent ? theme.palette.background.paper : 'transparent',
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    border: `1px solid ${theme.palette.grey[200]}`,
    borderWidth: alertType ? '2px' : '1px',
    borderColor:
      alertType === 'warning'
        ? theme.palette.warning.light
        : alertType === 'error'
          ? theme.palette.error.light
          : theme.palette.grey[200],
    boxShadow: theme.shadows[1],
    overflow: 'hidden',
    [theme.breakpoints.down('md')]: {
      ...(hasBorder
        ? {}
        : {
            borderRadius: 0,
            border: 'none',
            boxShadow: 'none'
          }),
      marginBottom: 0
    }
  }),

  accordionContent: (expandedWithAlert: boolean) => ({
    borderRadius: expandedWithAlert ? theme.spacing(2) : theme.spacing(2, 2, 0, 0),
    overflow: 'hidden',
    width: '100%'
  }),

  accordionContainerSx: (
    expanded: boolean,
    expandedWithAlert: boolean,
    separateHeader: boolean = false,
    maxWidth?: string,
    extraMarginBottom: boolean = true
  ) => ({
    width: '100%',
    '& .MuiAccordion-region': { height: expanded ? 'auto' : 0 },
    '& .MuiAccordionDetails-root': { display: expanded ? 'block' : 'none' },
    boxShadow: 'none',
    '&.Mui-expanded': {
      marginBottom: expandedWithAlert || !extraMarginBottom ? 0 : theme.spacing(2)
    },
    '& .MuiAccordionSummary-contentGutters': {
      margin: 0,
      [theme.breakpoints.down('md')]: {
        margin: 0,
        maxWidth: maxWidth
      }
    },
    '& .MuiAccordionSummary-root': {
      [theme.breakpoints.down('md')]: {
        justifyContent: 'flex-start'
      }
    },
    [theme.breakpoints.down('md')]: {
      backgroundColor: separateHeader ? 'transparent' : theme.palette.background.paper
    }
  }),

  accordionHeader: (
    expandIconPosition: 'left' | 'right',
    noExpandBackgroundColor: boolean,
    separateHeader: boolean,
    extraPaddingBottom: boolean = true
  ) => ({
    display: 'flex',
    flexDirection: expandIconPosition === 'left' ? 'row-reverse' : 'row',
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(extraPaddingBottom ? 2 : 0),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    '& .MuiAccordionSummary-expandIconWrapper': {
      marginRight: theme.spacing(2)
    },
    '&.Mui-expanded': {
      paddingTop: theme.spacing(2),
      backgroundColor: noExpandBackgroundColor ? 'transparent' : theme.palette.grey[200],
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      [theme.breakpoints.down('md')]: {
        backgroundColor: separateHeader ? theme.palette.grey[200] : 'transparent',
        paddingLeft: 0,
        paddingRight: 0,
        minHeight: 'unset',
        paddingBottom: theme.spacing(2),
        borderRadius: separateHeader ? '16px' : undefined
      }
    },
    [theme.breakpoints.down('md')]: {
      paddingLeft: separateHeader ? theme.spacing(1) : 0,
      paddingRight: 0,
      minHeight: 'unset',
      width: '100%',
      backgroundColor: separateHeader ? theme.palette.grey[200] : theme.palette.background.paper,
      borderRadius: separateHeader ? '16px' : undefined,
      '&.Mui-expanded': {
        paddingLeft: separateHeader ? theme.spacing(1) : 0
      }
    }
  }),

  accordionHeaderContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },

  accordionHeaderMainContent: (fullWidth?: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    width: fullWidth ? '100%' : undefined
  }),

  accordionHeaderButtons: (width: string | null = '100%') => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      paddingBottom: theme.spacing(width && width !== 'auto' ? 2 : 0),
      width: width,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      justifyContent: 'flex-start'
    }
  }),

  accordionDetails: (hasPadding?: boolean) => ({
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      ...(hasPadding ? {} : { paddingLeft: 0, paddingRight: 0 })
    }
  }),

  accordionFooterContainer: (alertType?: AccordionAlertType) => ({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor:
      alertType === 'warning'
        ? theme.palette.warning.light
        : alertType === 'error'
          ? theme.palette.error.light
          : 'transparent'
  }),

  accordionFooterContent: (isAlert: boolean) => ({
    padding: theme.spacing(isAlert ? 2 : 3),
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'unset'
    }
  }),

  accordionFooterAlertText: (alertType?: AccordionAlertType) =>
    ({
      fontSize: theme.typography.body1.fontSize,
      textAlign: 'left',
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      color:
        alertType === 'warning'
          ? theme.palette.warning.contrastText
          : alertType === 'error'
            ? theme.palette.error.contrastText
            : theme.palette.text.primary
    }) as CSSProperties,

  accordionFooterAlertIcon: {
    fontSize: theme.typography.body1.fontSize
  },

  accordionFooterButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      flexWrap: 'wrap'
    }
  },

  accordionFooterButton: (alertType?: AccordionAlertType) => {
    let style = {} as CSSProperties

    if (alertType === 'error') {
      style = {
        color: theme.palette.error.contrastText,
        backgroundColor: 'transparent',
        border: `1.5px solid ${theme.palette.error.contrastText}`
      } as CSSProperties
    }

    return style
  },

  accordionFooterDivider: {
    width: '100%',
    [theme.breakpoints.down('md')]: {
      marginX: theme.spacing(2),
      width: 'auto'
    }
  },

  accordionHeaderTertiaryContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: theme.spacing(2),
    paddingBottom: theme.spacing(2)
  }
}

export default styles
